import type { AuthUser } from "./auth";
import { isValidAtomMsisdn } from "./crbt";
import { isUserPremium } from "./quota";

export interface PremiumPromotion {
  campaignId: string;
  productCode: string;
  price: number;
  originalPrice: number;
  durationHours: number;
}

const env = import.meta.env;

function parseBoolean(value: string | undefined, fallback = false): boolean {
  if (value === undefined || value === "") return fallback;
  return value.toLowerCase() === "true";
}

function parsePositiveNumber(value: string | undefined, fallback: number): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

function promotionExclusionList(): Set<string> {
  return new Set(
    String(env.VITE_PREMIUM_PROMOTION_EXCLUDED_USER_IDS || "")
      .split(",")
      .map((id) => id.trim())
      .filter(Boolean)
  );
}

function isGoogleAccount(user: AuthUser): boolean {
  const provider = String(
    user.auth_provider || user.provider || user.login_provider || ""
  ).toLowerCase();
  return provider === "google" || /@gmail\.com$/i.test(String(user.email || "").trim());
}

function atomMsisdnFor(user: AuthUser): string {
  const candidates = [
    user.msisdn,
    user.phone_number,
    user.mobile_number,
    user.username,
  ];

  return candidates
    .map((value) => String(value || "").trim())
    .find(isValidAtomMsisdn) || "";
}

/**
 * Environment-backed promotion adapter used until the backend eligibility API
 * is available. Production remains opt-in; local development defaults to the
 * mock campaign so the complete experience can be reviewed without an API.
 */
export function getEligiblePremiumPromotion(user?: AuthUser | null): PremiumPromotion | null {
  const enabled = parseBoolean(env.VITE_PREMIUM_PROMOTION_ENABLED, Boolean(import.meta.env.DEV));
  if (
    !enabled ||
    !user ||
    isUserPremium() ||
    isGoogleAccount(user) ||
    (!user.is_guest && (!atomMsisdnFor(user) || user.is_new_user === false))
  ) return null;

  const mockEligible = parseBoolean(
    env.VITE_PREMIUM_PROMOTION_MOCK_ELIGIBLE,
    Boolean(import.meta.env.DEV)
  );
  const userId = String(user.id);
  const isExcluded = promotionExclusionList().has(userId);

  if (!mockEligible || isExcluded) return null;

  return {
    campaignId: env.VITE_PREMIUM_PROMOTION_CAMPAIGN_ID || "premium-1499-launch",
    productCode: env.VITE_PREMIUM_PROMOTION_PRODUCT_CODE || "YRMUSIC_1499S",
    price: parsePositiveNumber(env.VITE_PREMIUM_PROMOTION_PRICE, 1500),
    originalPrice: parsePositiveNumber(env.VITE_PREMIUM_PROMOTION_ORIGINAL_PRICE, 2000),
    durationHours: parsePositiveNumber(env.VITE_PREMIUM_PROMOTION_DURATION_HOURS, 24),
  };
}

export function getPremiumPromotionDeadline(
  promotion: PremiumPromotion,
  user?: AuthUser | null,
  now = Date.now()
): number {
  const audienceKey = user?.id === undefined || user?.id === null ? "guest" : String(user.id);
  const storageKey = `ym_promotion_deadline_${promotion.campaignId}_${audienceKey}`;

  try {
    const stored = Number(localStorage.getItem(storageKey));
    if (Number.isFinite(stored) && stored > 0) return stored;

    const deadline = now + promotion.durationHours * 60 * 60 * 1000;
    localStorage.setItem(storageKey, String(deadline));
    return deadline;
  } catch {
    return now + promotion.durationHours * 60 * 60 * 1000;
  }
}
